/**
 * Background push of a ServiceBox dossier to the garage's Alpha DMS.
 *
 * The dossier goes to Alpha as soon as the RDV exists on ServiceBox: the client and the
 * vehicle land in Alpha right away and the OR number comes back. Alpha prints the CT when
 * it creates an OR — but it picks the printer PER POSTE in its own config, and nothing in
 * the message (IMPRIM, valorisation) changes that (measured on the printer's job log,
 * 2026-09-23). So Carvis stamps its own poste on the message (`ALPHA_CARVIS_POSTE`), which
 * the garage's Alpha server routes to a paperless printer; the garage's own transfers keep
 * their poste and their paper CT.
 *
 * It runs outside any component: closing the transfer modal does not stop it, and the OR
 * number lands on the ordre when Alpha answers. Once the request has reached the proxy the
 * push completes even if the tab closes — only writing the number back is lost, and the
 * nightly ServiceBox sync recovers it.
 */
import { create } from "zustand";
import { serviceboxProxy, type ServiceBoxCredentials, type StepResult } from "@services/serviceboxProxy";
import { ordresApi } from "@services/api/ordres";
import { useOrdreStore } from "@store/ordreStore";
import { showNotification } from "context/notification.service";
import { setBackgroundTask } from "@services/backgroundTasks";
import type { Ordre } from "types/ordre";
import { parseAddress } from "@utils/serviceboxTransfer";
import { knownCivilite, sbClientIdentity } from "@utils/sbClientIdentity";

export interface AlphaTransferJob {
  // waiting = queued behind another operation on the same ServiceBox account
  status: "running" | "waiting" | "done" | "error";
  steps: StepResult[];
  orNumber?: string;
  error?: string;
  /** The dossier reached Alpha but no OR number came back: Alpha may have made the OR. */
  unconfirmed?: boolean;
}

/** Shown wherever a send reached Alpha without an OR number coming back. */
export const ALPHA_UNCONFIRMED_MESSAGE =
  "Alpha a peut-être déjà créé l'OR : vérifiez dans Alpha avant de renvoyer le dossier.";

interface AlphaTransferStore {
  jobs: Record<string, AlphaTransferJob>;
}

/** NumeroPoste stamped on Carvis-driven Alpha transfers. Must match the [IMPRESSION_<poste>]
 *  section of the eDMS INI files on the garage's Alpha server (LASERPEU.INI, CT-CARVIS printer)
 *  and `ALPHA_CARVIS_POSTE` in the backend. Proxy >= 3.52.0. */
export const ALPHA_CARVIS_POSTE = "010";

/** Poste des dossiers carrosserie : Alpha choisit le DÉPARTEMENT de l'OR par poste
 *  ([OR_LASER<poste>] des INI eDMS). 010 retombe sur [OR_LASER] = Atelier mécanique ; 011
 *  copie [OR_LASER003] (Carrosserie) et garde l'impression PDF de 010 (Steeven, 07/10/2026). */
export const ALPHA_CARVIS_POSTE_CARROSSERIE = "011";

/** Le poste Alpha d'un ordre : 011 pour un dossier carrosserie, sinon 010. */
export function alphaPosteFor(ordre: Pick<Ordre, "booking_request"> & { reception_category?: string | null }): string {
  const br = ordre.booking_request || {};
  const carrosserie =
    String(ordre.reception_category || "").toLowerCase().startsWith("carrosserie") ||
    [br.service, br.intervention_type].some((v) => String(v || "").toLowerCase() === "carrosserie");
  return carrosserie ? ALPHA_CARVIS_POSTE_CARROSSERIE : ALPHA_CARVIS_POSTE;
}

/** Alpha transfer jobs keyed by ordre id. In-memory only: a job does not survive a reload. */
export const useAlphaTransferStore = create<AlphaTransferStore>(() => ({ jobs: {} }));

const isActive = (job?: AlphaTransferJob) => job?.status === "running" || job?.status === "waiting";

function setJob(ordreId: string, job: AlphaTransferJob) {
  useAlphaTransferStore.setState((s) => ({ jobs: { ...s.jobs, [ordreId]: job } }));
  syncUnloadGuard();
}

// Leaving mid-transfer loses the OR number write-back: ask the browser to confirm.
let guardOn = false;
const unloadGuard = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = "";
};
function syncUnloadGuard() {
  const active = Object.values(useAlphaTransferStore.getState().jobs).some(isActive);
  if (active === guardOn) return;
  guardOn = active;
  if (active) window.addEventListener("beforeunload", unloadGuard);
  else window.removeEventListener("beforeunload", unloadGuard);
}

function failureMessage(relayState: string | null | undefined, error: string): string {
  if (relayState === "unreachable") {
    return "RDV créé sur ServiceBox, mais le relais Alpha est injoignable : le dossier n'est pas parti. Le garage peut le transférer depuis ServiceBox.";
  }
  if (relayState === "ambiguous") {
    return `Le dossier est parti vers Alpha, mais aucun numéro d'OR n'est revenu. ${ALPHA_UNCONFIRMED_MESSAGE}`;
  }
  return `RDV créé sur ServiceBox, mais l'envoi vers Alpha a échoué : ${error}`;
}

export interface StartAlphaTransferParams {
  ordre: Ordre;
  dossierId: string;
  creds: ServiceBoxCredentials;
  /** Same contract as ServiceBoxTransferModal.onTransferred — may fire after the modal closed. */
  onTransferred?: (updates: Partial<Ordre>) => void;
  /** No success toast (pushing a client edit onto an already-transferred RDV). Failures always toast. */
  quiet?: boolean;
  /** RDV ServiceBox that pre-dates this push (imported RDV, or a retry): the proxy reads the
   *  OR already on it instead of blindly asking Alpha for a second one. */
  rdvId?: string;
  /** false = send the dossier without valorising it, so Alpha creates the OR with no montant.
   *  The garage prices the OR in Alpha. Requires proxy >= 3.43.0. */
  valorise?: boolean;
  /** false = IMPRIM=0 in the message: Alpha is asked not to print the CT at OR creation.
   *  The transfer is otherwise unchanged. Requires proxy >= 3.46.0. */
  imprim?: boolean;
  /** NumeroPoste in the message — `ALPHA_CARVIS_POSTE` routes the CT to the paperless
   *  printer on the Alpha server. Undefined = the poste ServiceBox built (paper CT). */
  poste?: string;
  /** Send even though an earlier send reached Alpha without a confirmed OR — only once
   *  someone has checked Alpha ("J'ai vérifié, renvoyer"). Requires proxy >= 3.60.0. */
  force?: boolean;
}

/** Marks (or clears, with null) "sent to Alpha, OR unconfirmed" on the ordre. Best-effort:
 *  the proxy's own journal still refuses a second send if this write is lost. */
async function setUnconfirmedMark(ordreId: string, at: string | null) {
  try {
    const updated = await ordresApi.update(ordreId, { alpha_sent_unconfirmed_at: at } as any);
    useOrdreStore.getState().updateOrdre(updated);
  } catch {
    /* the proxy guard is the real safety net */
  }
}

/** E-mail and street of the ordre's client, for a client Alpha creates from this transfer
 *  (ServiceBox drops both from its message — test client 1018079, 2026-10-18). */
function clientContact(ordre: Ordre): { clientEmail?: string; clientAdresse?: string; clientCivilite?: string } {
  const c: any = ordre.contacts?.find((x) => x.is_primary) || ordre.contacts?.[0] || {};
  const rue = (c.adresse_rue || parseAddress(c.adresse).rue || "").trim();
  const civ = knownCivilite(c, sbClientIdentity(null, c, ordre.name || "").civilite_id);
  return { clientEmail: (c.email || "").trim() || undefined, clientAdresse: rue || undefined, clientCivilite: civ || undefined };
}

/** Fire-and-forget: never rejects, the outcome is in the job + a toast. No-op if one is already running. */
export async function startAlphaTransfer({ ordre, dossierId, creds, onTransferred, quiet, rdvId, valorise, imprim, poste, force }: StartAlphaTransferParams): Promise<void> {
  const ordreId = ordre.id;
  if (isActive(useAlphaTransferStore.getState().jobs[ordreId])) return;
  setJob(ordreId, { status: "running", steps: [] });
  // The push outlives the modal that started it, so it gets a line of its own on screen:
  // 15-25 s of Alpha's time is long enough to walk away from.
  const taskId = `alpha:${ordreId}`;
  const taskLabel = `Envoi vers Alpha — ${ordre.immatriculation || ordre.num || ""}`.trim();
  setBackgroundTask(taskId, { label: taskLabel, status: "running", detail: "Envoi du dossier vers Alpha…" });

  try {
    const result = await serviceboxProxy.transferToAlpha(creds, dossierId, () => {
      setJob(ordreId, { status: "waiting", steps: [] });
      setBackgroundTask(taskId, { status: "waiting", detail: "En attente du proxy ServiceBox…" });
    }, { rdvId, checkExisting: !!rdvId, valorise, imprim, poste, force, ...clientContact(ordre) });

    const orNumber = result.or_number || undefined;
    // Sent, but no OR number back — lost answer, or Alpha answering without one (a proxy
    // < 3.60.0 calls that a success). Alpha may have created the OR: FP-986-SK got three ORs
    // from three clicks (13/10/2026). Mark the ordre so the button waits for a check in Alpha.
    const unconfirmed = !orNumber && (result.relay_state === "ambiguous" || result.success);
    if (unconfirmed) {
      const error = failureMessage("ambiguous", "");
      setJob(ordreId, { status: "error", steps: result.steps || [], error, unconfirmed: true });
      setBackgroundTask(taskId, { status: "error", detail: "envoyé, numéro d'OR non confirmé" });
      await setUnconfirmedMark(ordreId, new Date().toISOString());
      showNotification({ type: "warning", message: error });
      return;
    }

    if (!result.success) {
      const error = failureMessage(result.relay_state, result.error || "erreur inconnue");
      setJob(ordreId, { status: "error", steps: result.steps || [], error });
      setBackgroundTask(taskId, { status: "error", detail: result.error || "échec de l'envoi" });
      showNotification({ type: "error", message: error });
      return;
    }

    // Re-sending a dossier that already has its OR returns the same number: an update, not a creation.
    const isNewOr = !!orNumber && orNumber !== ordre.or_number;
    if (!isNewOr && orNumber && ordre.alpha_sent_unconfirmed_at) {
      await setUnconfirmedMark(ordreId, null);
    }
    if (isNewOr) {
      // The OR is here: whatever an earlier lost answer left unconfirmed is settled.
      const updates: Partial<Ordre> = { or_number: orNumber, num: `OR-${orNumber}`, alpha_sent_unconfirmed_at: null };
      try {
        const updated = await ordresApi.update(ordreId, updates as any);
        useOrdreStore.getState().updateOrdre(updated);
        onTransferred?.(updates);
      } catch {
        // The OR exists in Alpha; only our copy of its number is missing. Not a transfer
        // failure — do not invite a retry.
        setJob(ordreId, { status: "done", steps: result.steps || [], orNumber });
        showNotification({
          type: "warning",
          message: `OR n°${orNumber} créé dans Alpha, mais son numéro n'a pas pu être enregistré sur l'ordre. Saisissez-le à la main ou attendez la synchro de nuit.`,
        });
        return;
      }
    }
    setJob(ordreId, { status: "done", steps: result.steps || [], orNumber });
    setBackgroundTask(taskId, { status: "done", detail: `Dans Alpha — OR n°${orNumber}` });
    if (!quiet) {
      showNotification({
        type: "success",
        message: result.relay_state === "already_transferred"
          ? `OR n°${orNumber} déjà créé dans Alpha : dossier non renvoyé`
          : valorise === false
            ? `Client envoyé dans Alpha (OR n°${orNumber}, sans montant — à chiffrer dans Alpha)`
            : isNewOr
              ? `OR n°${orNumber} créé dans Alpha${poste ? ", sans impression" : ""}`
              : `Alpha mis à jour (OR n°${orNumber})`,
      });
    }
  } catch (e: any) {
    const error = failureMessage(null, e?.message || "proxy injoignable");
    setJob(ordreId, { status: "error", steps: [], error });
    setBackgroundTask(taskId, { status: "error", detail: e?.message || "proxy injoignable" });
    showNotification({ type: "error", message: error });
  }
}
